import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, FileText, Download, ArrowRight } from "lucide-react";
import { Disclaimer } from "@/components/site/Disclaimer";
import { NonReservedNotice } from "@/components/site/NonReservedNotice";

import { ARTICLES, TEMPLATES } from "@/lib/resources-content";

export const Route = createFileRoute("/resources/")({
  head: () => ({
    meta: [
      { title: "Resource Centre | Legal Insights, Guides & Templates" },
      {
        name: "description",
        content:
          "Free guides, plain-English explainers and downloadable templates on labour relations, CCMA, POPIA, contracts and debt — from Nkadimeng Danny Legal & Compliance Support.",
      },
      { property: "og:title", content: "Resource Centre" },
      {
        property: "og:description",
        content:
          "Plain-English guides, explainers and templates on South African labour, compliance and consumer matters.",
      },
    ],
  }),
  component: ResourcesPage,
});

function ResourcesPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Resource centre
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Free, plain-English guides, explainers and downloadable templates
            on labour, compliance, consumer and litigation matters in South
            Africa.
          </p>
          <div className="mt-6 max-w-3xl space-y-3">
            <NonReservedNotice />
            <Disclaimer variant="compact" />
          </div>

        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="flex items-end justify-between gap-4">
          <div>
            <span className="gold-rule" />
            <h2 className="font-display text-3xl text-navy">Guides &amp; insights</h2>
          </div>
          <span className="hidden text-sm text-muted-foreground sm:inline">
            New articles published monthly
          </span>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {ARTICLES.map((a) => (
            <Link
              key={a.slug}
              to="/resources/$slug"
              params={{ slug: a.slug }}
              className="flex h-full flex-col rounded-xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-lifted"
            >
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded-full bg-navy/10 px-2.5 py-0.5 font-medium text-navy">
                  {a.tag}
                </span>
                <span className="text-muted-foreground">{a.eta}</span>
              </div>
              <h3 className="mt-4 font-display text-lg leading-snug text-navy">
                {a.title}
              </h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                {a.summary}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-gold">
                Read article <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-secondary/50 py-16">
        <div className="container-narrow">
          <span className="gold-rule" />
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl text-navy">Downloadable templates</h2>
              <p className="mt-3 max-w-2xl text-muted-foreground">
                Starter templates for common documents. Each is a
                <strong> starting point</strong> — review and adapt to your
                facts, or book a consultation for a tailored version.
              </p>
            </div>
            <Link
              to="/contracts"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
            >
              <FileText className="h-4 w-4" /> Branded contract library (PDF)
            </Link>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TEMPLATES.map((t) => (
              <div
                key={t.title}
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-4 shadow-soft"
              >
                <div className="flex items-start gap-3">
                  <FileText className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  <div>
                    <p className="text-sm font-medium text-navy">{t.title}</p>
                    <p className="text-xs text-muted-foreground">{t.format}</p>
                  </div>
                </div>
                <a
                  href={t.filename}
                  download
                  className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
                >
                  Download <Download className="h-3.5 w-3.5" />
                </a>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Looking for a fully-branded PDF contract you can sign today?{" "}
            <Link to="/contracts" className="text-navy underline">
              Browse the contract library
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="rounded-2xl bg-navy p-10 text-navy-foreground">
          <div className="flex items-start gap-4">
            <BookOpen className="h-7 w-7 shrink-0 text-gold" />
            <div>
              <h2 className="font-display text-2xl text-navy-foreground">
                Want a guide on something specific?
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-navy-foreground/80">
                Email us with the topic you're stuck on. If it's useful to
                others too, we'll publish a free explainer.
              </p>
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-gold-foreground"
              >
                Suggest a topic <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}